import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'

import {
  adminCreateTestDefinition,
  fetchQualityTests,
  fetchTestExecutionById,
  fetchTestHistory,
  restoreTestExecution,
  softDeleteTestExecution,
} from '../../api/services/qualityControlService'
import { qualityTestService } from '../../api/services/qualityTest.service'
import { UnauthorizedError } from '../../auth/UnauthorizedError'
import { clearAuth } from '../../store/authSlice'
import { useAppDispatch, useCurrentSite } from '../../store/store.hooks'
import { CreateQcTestDefinitionPayload, QcTestExecutionSubmission } from '../../types'

export const useCreateTestDefinition = (onError?: (msg: string) => void) => {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const currentSite = useCurrentSite()

  return useMutation({
    mutationFn: (payload: CreateQcTestDefinitionPayload) => adminCreateTestDefinition(currentSite?.id ?? '', payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quality-tests', 'list'] })
      navigate('/quality-control')
    },
    onError: (error) => {
      const axiosData = (error as { response?: { data?: { detail?: string; title?: string } } })?.response?.data
      const msg =
        axiosData?.detail ?? axiosData?.title ?? (error instanceof Error ? error.message : 'An error occurred')
      onError?.(msg)
    },
  })
}

export const useQualityTestList = () => {
  const currentSite = useCurrentSite()

  const { data } = useSuspenseQuery({
    queryKey: ['quality-tests', 'list', currentSite?.id],
    queryFn: () => fetchQualityTests(currentSite?.id ?? ''),
    staleTime: 5 * 60 * 1000,
    refetchOnMount: true,
    refetchInterval: 60 * 1000,
  })

  return { qualityTests: data }
}

export const useQualityTest = (testId: string) => {
  const currentSite = useCurrentSite()

  const { data } = useSuspenseQuery({
    queryKey: ['quality-tests', currentSite?.id, testId],
    queryFn: () => {
      if (!currentSite?.id || !testId) return new Promise(() => {})
      return qualityTestService.get(currentSite?.id ?? '', testId)
    },
    staleTime: 5 * 60 * 1000,
  })

  return { qualityTest: data, isLoading: !data, isError: false }
}

export const useSubmitTestExecution = () => {
  const dispatch = useAppDispatch()
  const queryClient = useQueryClient()
  const currentSite = useCurrentSite()

  return useMutation({
    mutationFn: ({
      testVersionId,
      submission,
    }: {
      testVersionId: string
      testGroupId: string
      submission: QcTestExecutionSubmission
    }) => qualityTestService.submit(currentSite?.id ?? '', testVersionId, submission),
    onSuccess: (_data, variables) => {
      // Remove the versions cache for this test group so the editor's useSuspenseQuery
      // re-fetches fresh data (including updated hasExecutions) rather than briefly
      // rendering stale cached data with hasExecutions: false.
      queryClient.removeQueries({ queryKey: ['quality-tests', variables.testGroupId, 'versions'] })
      queryClient.invalidateQueries({ queryKey: ['quality-tests'] })
      queryClient.invalidateQueries({ queryKey: ['quality-test-history'] })
    },
    onError: (error) => {
      if (error instanceof UnauthorizedError) {
        dispatch(clearAuth())
      }
    },
  })
}

export const useTestHistory = (testId: string, page: number) => {
  const queryClient = useQueryClient()
  const currentSite = useCurrentSite()

  const pageSize = 25

  const { data } = useSuspenseQuery({
    queryKey: ['quality-test-history', testId, page],
    queryFn: () => fetchTestHistory(currentSite?.id ?? '', testId, page, pageSize),
    staleTime: 60 * 1000,
    refetchOnMount: true,
    refetchInterval: 30 * 1000,
  })

  // Prefetch the next page so navigating forward does not trigger the Suspense fallback
  queryClient.prefetchQuery({
    queryKey: ['quality-test-history', testId, page + 1],
    queryFn: () => fetchTestHistory(currentSite?.id ?? '', testId, page + 1, pageSize),
    staleTime: 60 * 1000,
  })

  return { history: data, pageSize }
}

export const useSoftDeleteTestExecution = (onError?: (msg: string) => void) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (executionId: string) => softDeleteTestExecution(executionId),
    onSuccess: (_, executionId) => {
      queryClient.invalidateQueries({ queryKey: ['quality-test-history'] })
      queryClient.invalidateQueries({ queryKey: ['quality-tests'] })
      queryClient.invalidateQueries({ queryKey: ['quality-test-execution', executionId] })
    },
    onError: (error) => {
      const msg = error instanceof Error ? error.message : 'An error occurred'
      onError?.(msg)
    },
  })
}

export const useRestoreTestExecution = (onError?: (msg: string) => void) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (executionId: string) => restoreTestExecution(executionId),
    onSuccess: (_, executionId) => {
      queryClient.invalidateQueries({ queryKey: ['quality-test-history'] })
      queryClient.invalidateQueries({ queryKey: ['quality-tests'] })
      queryClient.invalidateQueries({ queryKey: ['quality-test-execution', executionId] })
    },
    onError: (error) => {
      const msg = error instanceof Error ? error.message : 'An error occurred'
      onError?.(msg)
    },
  })
}

export const useTestExecution = (executionId: string) => {
  const currentSite = useCurrentSite()

  const { data } = useSuspenseQuery({
    queryKey: ['quality-test-execution', currentSite?.id, executionId],
    queryFn: () => fetchTestExecutionById(currentSite?.id ?? '', executionId),
    staleTime: 5 * 60 * 1000,
  })

  return { execution: data }
}
